import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Input, Checkbox, Card, PageHeader, Button } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import NumberInput from "components/cck/NumberInput";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Language } from "imports/Utils";

import { connectingVesselSchema, ConnectingVesselSchemaInput, ConnectingVesselSchemaOutput } from "./utils/connectingVessel.schema";

import { GetConnectingVesselDocument } from "__generated__/view/connectingVessels/graphql/connectingVessel.queries.generated";
import { SaveConnectingVesselDocument } from "__generated__/view/connectingVessels/graphql/connectingVessel.mutations.generated";

const ConnectingVesselCreateEdit = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const [ getConnectingVessel ] = useLazyQuery(GetConnectingVesselDocument);
	const [ saveConnectingVessel, { loading } ] = useMutation(SaveConnectingVesselDocument);

	const { control, handleSubmit, reset } = useForm<ConnectingVesselSchemaInput, unknown, ConnectingVesselSchemaOutput>({
		resolver: zodResolver(connectingVesselSchema),
		defaultValues: {
			id: null,
			name: "",
			mmsi: null,
			imo: null,
			callSign: "",
			flagName: "",
			flagCode: "",
			tracking: false,
		},
	});

	const save = (data: ConnectingVesselSchemaOutput) => {
		saveConnectingVessel({
			variables: {
				input: {
					...data,
				},
			},
		}).then(() => {
			navigate("/view/connectingvessels");
		});
	};

	useEffect(() => {
		if (id) {
			getConnectingVessel({
				variables: {
					id: Number(id),
				},
			}).then(response => {
				if (response.data?.connectingVessel) {
					reset({
						...response.data.connectingVessel,
						tracking: response.data.connectingVessel.tracking ?? false,
					});
				}
			});
		}
	}, []);

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("CONNECTING_VESSELS")}
					<Helmet title={intl.formatMessage({ id: "CONNECTING_VESSELS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/connectingvessels")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12, lg: 6 }}>
					<Card>
						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VESSEL_NAME" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name='name'
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} autoFocus />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VESSEL_MMSI" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name='mmsi'
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput {...field} decimalScale={0} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VESSEL_IMO" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name='imo'
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput {...field} decimalScale={0} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VESSEL_CALLSIGN" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name='callSign'
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VESSEL_FLAGNAME" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name='flagName'
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VESSEL_FLAGCODE" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name='flagCode'
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "CONNECTING_VESSEL_TRACKING" })}
							labelPlacement="left-center"
							overflow="hidden">
							<Controller
								name="tracking"
								control={control}
								render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value ?? false}  />} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default ConnectingVesselCreateEdit;
